import { useState, useEffect } from "react";
import { ArrowRight, Download, MessageCircle } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Reveal } from "../components/common/Reveal";
import { waLink, WHATSAPP_NUMBER } from "../data/config";
import { track } from "../lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERTICALS = [
  { id: "consulting", label: "Consulting" },
  { id: "dental", label: "Dental" },
  { id: "creative", label: "Creative" },
];

const PATHWAYS = {
  consulting: [
    "I need a speaker / trainer",
    "I want to develop my team",
    "I need business consulting",
    "I need branding / marketing help",
    "I need advertising solutions",
    "I want Growth Factor for my organisation",
    "Other",
  ],
  dental: [
    "Conventional Braces",
    "Clear Aligners",
    "FRC",
    "Zirconia Crowns & Bridges",
    "Veneers / Smile Makeover",
    "I'm not sure which treatment I need",
  ],
  creative: ["Book purchase", "Rights / publishing", "Collaboration", "Other"],
};

const INITIAL = {
  name: "",
  organisation: "",
  role: "",
  email: "",
  phone: "",
  enquiry_type: "",
  participants: "",
  preferred_date: "",
  message: "",
};

const inputCls =
  "w-full border border-ink/20 bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/40 focus:border-amber";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/60">
      {label}
    </span>
    {children}
  </label>
);

const utmParams = () => {
  const q = new URLSearchParams(window.location.search);
  return {
    utm_source: q.get("utm_source") || "",
    utm_medium: q.get("utm_medium") || "",
    utm_campaign: q.get("utm_campaign") || "",
    utm_content: q.get("utm_content") || "",
    utm_term: q.get("utm_term") || "",
    gclid: q.get("gclid") || "",
  };
};

export default function ContactPage() {
  const [vertical, setVertical] = useState("consulting");
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const getSession = () => {
    let sid = sessionStorage.getItem("aira_session");
    if (!sid) {
      sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("aira_session", sid);
    }
    return sid;
  };

  useEffect(() => {
    const sid = sessionStorage.getItem("aira_session");
    if (!sid) return;
    fetch(`${API}/leads/state/${sid}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (!s) return;
        setForm((prev) => ({
          ...prev,
          name: prev.name || s.name || "",
          phone: prev.phone || s.phone || "",
          preferred_date: prev.preferred_date || s.preferred_date || "",
          message: prev.message || s.patient_concern || "",
        }));
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.enquiry_type) {
      toast.error("Please choose the option that best fits your enquiry.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, vertical, source_page: "/contact", session_id: getSession(), ...utmParams() }),
      });
      if (!res.ok) throw new Error("Request failed");
      const meta = { source: "contact_form", enquiry_type: form.enquiry_type, page: "/contact" };
      if (vertical === "consulting") track("consulting_lead", meta);
      else if (vertical === "creative") track("creative_lead", meta);
      else if (vertical === "dental") track("aira_appointment_request_completed", { source: "contact_form", treatment_category: (form.enquiry_type || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""), page: "/contact" });
      toast.success("Thank you — your enquiry has been received. We will be in touch shortly.");
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <section className="bg-warmwhite py-24 lg:py-32" data-testid="contact-hero">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Contact</p>
            <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              HAVE A CHALLENGE <span className="italic text-burgundy">WORTH SOLVING?</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
              One hotline, three worlds. Tell us where you are and what you want
              to achieve — every enquiry is tagged to its vertical so the right
              person responds.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28">
        <div className="container-site grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">
              Which world is this about?
            </p>
            <div className="mt-4 flex gap-2" role="group" aria-label="Vertical">
              {VERTICALS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => { setVertical(v.id); setForm({ ...form, enquiry_type: "" }); }}
                  aria-pressed={vertical === v.id}
                  data-testid={`vertical-${v.id}`}
                  className={`border px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                    vertical === v.id ? "border-burgundy bg-burgundy/5 text-burgundy" : "border-ink/15 text-ink/70 hover:border-amber"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <p className="mt-10 text-[10px] font-semibold uppercase tracking-[0.25em] text-ink/50">
              Start with your pathway
            </p>
            <div className="mt-4 flex flex-col gap-3" role="group" aria-label="Type of enquiry">
              {PATHWAYS[vertical].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setForm({ ...form, enquiry_type: option })}
                  aria-pressed={form.enquiry_type === option}
                  data-testid={`pathway-option-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`border px-5 py-4 text-left text-sm transition-colors duration-300 ${
                    form.enquiry_type === option
                      ? "border-burgundy bg-burgundy/5 text-burgundy"
                      : "border-ink/15 text-ink/75 hover:border-amber"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <div className="mt-10 border-t border-ink/10 pt-8">
              <a
                href={waLink(`Hi, I'm enquiring via the iINSPIRE website.\nSource: Website — Contact (${vertical})`)}
                target="_blank"
                rel="noreferrer"
                className="link-arrow"
                data-testid="contact-whatsapp-alt"
              >
                <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> Prefer WhatsApp? Message us directly
              </a>
              <div className="mt-8">
                <a
                  href="/files/irma-ariandi-corporate-profile.pdf"
                  download
                  className="btn-outline"
                  data-testid="download-profile-button"
                >
                  <Download className="h-4 w-4" strokeWidth={1.5} /> Download Company Profile
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8">
            <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="enquiry-form">
              <Field label="Name *">
                <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="input-name" placeholder="Your full name" />
              </Field>
              <Field label="Organisation">
                <input value={form.organisation} onChange={set("organisation")} className={inputCls} data-testid="input-organisation" placeholder="Company, clinic or institution" />
              </Field>
              <Field label="Role">
                <input value={form.role} onChange={set("role")} className={inputCls} data-testid="input-role" placeholder="Your position" />
              </Field>
              <Field label="Email *">
                <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="input-email" placeholder="you@company.com" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="input-phone" placeholder="+60 ..." />
              </Field>
              <Field label="Type of enquiry *">
                <input required readOnly value={form.enquiry_type} className={`${inputCls} cursor-not-allowed bg-softgrey`} data-testid="input-enquiry-type" placeholder="Choose a pathway on the left" />
              </Field>
              <Field label="Estimated participants">
                <input value={form.participants} onChange={set("participants")} className={inputCls} data-testid="input-participants" placeholder="If applicable" />
              </Field>
              <Field label="Preferred date">
                <input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="input-date" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Brief challenge / what you want to achieve *">
                  <textarea required rows={5} value={form.message} onChange={set("message")} className={inputCls} data-testid="input-message" placeholder="Tell us where you are, and where you want to be." />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={sending} className="btn-primary disabled:opacity-60" data-testid="submit-enquiry">
                  {sending ? "Sending…" : "Send Enquiry"} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
